import {
  Color,
  DoubleSide,
  Mesh,
  MeshPhysicalMaterial,
  PlaneGeometry,
  Uniform,
} from "three";
import {
  DEFAULT_UNDERWATER_VISUAL_SETTINGS,
  type UnderwaterVisualSettings,
} from "./VisualSettings";

const waterWaveGlsl = /* glsl */ `
  void computeWaterWave(out float wave, out float slopeX, out float slopeY) {
    vec2 centeredUv = (uv - vec2(0.5)) * 2.0;
    float t = time * waveSpeed;
    float radialDamp = 1.0 - smoothstep(0.7, 1.36, length(centeredUv));
    float dampMix = mix(0.62, 1.0, radialDamp);

    float phaseA = (position.x * 1.18 + t * 0.78) * waveScale;
    float waveA = sin(phaseA);
    float dWaveA_dx = cos(phaseA) * waveScale * 1.18;

    float phaseB = (position.y * 1.64 - t * 0.52) * waveScale + 1.7;
    float waveB = sin(phaseB);
    float dWaveB_dy = cos(phaseB) * waveScale * 1.64;

    float phaseC = (position.x * 0.74 + position.y * 0.94 + t * 0.44) * waveScale + 2.6;
    float waveC = sin(phaseC);
    float dWaveC_dx = cos(phaseC) * waveScale * 0.74;
    float dWaveC_dy = cos(phaseC) * waveScale * 0.94;

    float phaseRipple = (position.x * 3.2 - position.y * 2.25 + t * 1.34) * waveScale;
    float ripple = sin(phaseRipple);
    float dRipple_dx = cos(phaseRipple) * waveScale * 3.2;
    float dRipple_dy = cos(phaseRipple) * waveScale * -2.25;

    float phaseCounter = (position.x * -2.15 + position.y * 2.75 - t * 10.08) * waveScale + 0.9;
    float counterRipple = sin(phaseCounter);
    float dCounterRipple_dx = cos(phaseCounter) * waveScale * -2.15;
    float dCounterRipple_dy = cos(phaseCounter) * waveScale * 2.75;

    wave =
      (waveA * 0.42 + waveB * 0.27 + waveC * 0.18 + ripple * 0.08 + counterRipple * 0.05) *
      dampMix;
    float dWave_dx =
      (dWaveA_dx * 0.42 + dWaveC_dx * 0.18 + dRipple_dx * 0.08 + dCounterRipple_dx * 0.05) *
      dampMix;
    float dWave_dy =
      (dWaveB_dy * 0.27 + dWaveC_dy * 0.18 + dRipple_dy * 0.08 + dCounterRipple_dy * 0.05) *
      dampMix;

    slopeX = dWave_dx * waveStrength;
    slopeY = dWave_dy * waveStrength;
  }
`;

const waveVertexChunk = /* glsl */ `
  transformed.z += wave * waveStrength;
`;

const waveNormalVertexChunk = /* glsl */ `
  float wave;
  float slopeX;
  float slopeY;
  computeWaterWave(wave, slopeX, slopeY);
  vec3 waterNormal = normalize(vec3(-slopeX, -slopeY, 1.0));
  transformedNormal = waterNormal;
  objectNormal = waterNormal;
  #ifdef USE_TANGENT
  vec3 transformedTangent = normalize(vec3(1.0, 0.0, slopeX));
  objectTangent = vec4(transformedTangent, tangent.w);
  #endif
`;

const edgeFadeFragmentChunk = /* glsl */ `
  vec2 centered = abs(vUv - vec2(0.5)) * 2.0;
  float edgeBreakup =
    sin(vUv.x * 17.0 + vUv.y * 9.0 + time * 0.27) * 0.07 +
    sin(vUv.x * -11.0 + vUv.y * 21.0 - time * 0.18) * 0.05;
  float radialMask =
    1.0 -
    smoothstep(
      0.78 + edgeBreakup,
      1.18 + edgeBreakup * 0.6,
      length(centered * vec2(0.78, 1.04))
    );
  float squareFade =
    (1.0 - smoothstep(0.72, 1.0, centered.x)) *
    (1.0 - smoothstep(0.72, 1.0, centered.y));
  float edgeFade = radialMask * squareFade;

  diffuseColor.a *= edgeFade;
  if (diffuseColor.a <= 0.003) {
    discard;
  }
`;

function createWaterSurfaceMaterial(
  timeUniform: Uniform<number>,
  waveStrengthUniform: Uniform<number>,
  waveScaleUniform: Uniform<number>,
  waveSpeedUniform: Uniform<number>
) {
  const material = new MeshPhysicalMaterial({
    color: new Color(DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterSurfaceColor),
    transparent: true,
    opacity: DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterSurfaceOpacity,
    metalness: 0.05,
    roughness: 0.18,
    reflectivity: 0.32,
    envMapIntensity: 0.45,
    clearcoat: 0.38,
    clearcoatRoughness: 0.2,
    side: DoubleSide,
    depthWrite: false,
    depthTest: true,
  });

  material.onBeforeCompile = (shader) => {
    shader.uniforms.time = timeUniform;
    shader.uniforms.waveStrength = waveStrengthUniform;
    shader.uniforms.waveScale = waveScaleUniform;
    shader.uniforms.waveSpeed = waveSpeedUniform;

    shader.defines = {
      ...shader.defines,
      USE_UV: '1',
    }

    shader.vertexShader = shader.vertexShader.replace(
      "#include <common>",
      `#include <common>
        uniform float time;
        uniform float waveStrength;
        uniform float waveScale;
        uniform float waveSpeed;
        ${waterWaveGlsl}
      `
    );

    shader.vertexShader = shader.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>\n${waveVertexChunk}`
    );

    shader.vertexShader = shader.vertexShader.replace(
      "#include <defaultnormal_vertex>",
      `#include <defaultnormal_vertex>
      ${waveNormalVertexChunk}
      `
    );

    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <common>",
      `#include <common>
      uniform float time;`
    );

    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <opaque_fragment>",
      `
      ${edgeFadeFragmentChunk}
      #include <opaque_fragment>
      `
    );
  };

  material.customProgramCacheKey = () => "water-surface-physical";

  return material;
}

export class WaterSurface extends Mesh {
  private readonly waterMaterial: MeshPhysicalMaterial;
  private readonly timeUniform: Uniform<number>;
  private readonly waveStrengthUniform: Uniform<number>;
  private readonly waveScaleUniform: Uniform<number>;
  private readonly waveSpeedUniform: Uniform<number>;

  constructor(roomSize: number) {
    const timeUniform = new Uniform(0);
    const waveStrengthUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterSurfaceWaveStrength
    );
    const waveScaleUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterSurfaceWaveScale
    );
    const waveSpeedUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterSurfaceWaveSpeed
    );
    const geometry = new PlaneGeometry(roomSize * 10.8, roomSize * 10.8, 124, 124);
    geometry.computeTangents();
    const material = createWaterSurfaceMaterial(
      timeUniform,
      waveStrengthUniform,
      waveScaleUniform,
      waveSpeedUniform
    );

    super(geometry, material);

    this.waterMaterial = material;
    this.timeUniform = timeUniform;
    this.waveStrengthUniform = waveStrengthUniform;
    this.waveScaleUniform = waveScaleUniform;
    this.waveSpeedUniform = waveSpeedUniform;
    this.name = "water-surface";
    this.renderOrder = -1;
    this.rotation.set(-Math.PI / 2, 0, 0);
  }

  setSettings(settings: UnderwaterVisualSettings) {
    this.visible = settings.waterSurfaceOpacity > 0;
    this.position.y = settings.waterSurfaceHeight;
    this.waterMaterial.color.set(settings.waterSurfaceColor);
    this.waterMaterial.opacity = settings.waterSurfaceOpacity;
    this.waveStrengthUniform.value = settings.waterSurfaceWaveStrength;
    this.waveScaleUniform.value = settings.waterSurfaceWaveScale;
    this.waveSpeedUniform.value = settings.waterSurfaceWaveSpeed;
  }

  update(time: number) {
    this.timeUniform.value = time;
  }
}
